import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FC, useState, useRef, useEffect } from 'react';
import { LocationForm, LocationFormRef } from './LocationForm';
import { Location } from '@libs/schemas';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import { Loader } from 'src/components/Loader';
import { SentryMonitoringService } from '@libs/frontend-services';
import { MapPin, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { StatusDisplay } from 'src/components/StatusDisplay';

interface LocationDrawerProps {
  location?: Location | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const LocationDrawer: FC<LocationDrawerProps> = ({
  location,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<Location | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const formRef = useRef<LocationFormRef>(null);
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();
  const sentryMonitoringService = new SentryMonitoringService();
  const isEditing = !!location;

  // Reset state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setIsFormValid(false);
      setIsFormDirty(false);
      setFormValues(null);
    }
  }, [isOpen]);

  // Prevent sheet from closing when interacting with Google Places dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      // Check if clicking on Google Places dropdown
      if (target.closest('.pac-container')) {
        e.stopPropagation();
      }
    };

    // Use capture phase to intercept before Radix UI
    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [isOpen]);

  const createLocationMutation = trpc.admin.location.createLocation.useMutation({
    onSuccess: async () => {
      successToast({
        title: 'Location Created',
        description: 'The location has been successfully created.',
      });
      await utils.admin.location.getAllLocations.invalidate();
      setIsSubmitting(false);
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      errorToast({
        title: 'Error Creating Location',
        description: error.message || 'An error occurred while creating the location.',
      });
      setIsSubmitting(false);
    },
  });

  const updateLocationMutation = trpc.admin.location.updateLocation.useMutation({
    onSuccess: async () => {
      successToast({
        title: 'Location Updated',
        description: 'The location has been successfully updated.',
      });
      await utils.admin.location.getAllLocations.invalidate();
      if (location?._id) {
        await utils.admin.location.getLocation.invalidate(location._id);
      }
      setIsSubmitting(false);
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      errorToast({
        title: 'Error Updating Location',
        description: error.message || 'An error occurred while updating the location.',
      });
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (isEditing && location) {
        await updateLocationMutation.mutateAsync({
          id: location._id!,
          updates: {
            ...formValues!,
          },
        });
      } else {
        await createLocationMutation.mutateAsync({
          ...formValues!,
        });
      }
    } catch (error) {
      sentryMonitoringService.captureException(error as Error, { extra: { formValues } });
      errorToast({
        title: 'Error',
        description: 'An error occurred while saving the location.',
      });
      setIsSubmitting(false);
    }
  };

  const canSave = isFormValid && isFormDirty && !isSubmitting;

  const handleClose = () => {
    if (isFormDirty) {
      setShowConfirmDialog(true);
      return;
    }
    onClose();
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  if (!isOpen) {
    return null;
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Unknown';
    return format(new Date(date), "MMM do, yyyy 'at' h:mm a");
  };

  const confirmDialog = (
    <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogDescription>
            You have unsaved changes that will be lost if you leave now. Are you sure you want to
            continue without saving?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancelClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} className="bg-red-600 hover:bg-red-700">
            Leave without saving
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <SheetContent
          side="right"
          className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full"
          onInteractOutside={(e) => {
            // Prevent closing when clicking on Google Maps autocomplete dropdown
            const target = e.target as HTMLElement;
            // Check if the click is on the autocomplete dropdown or any of its children
            if (
              target.closest('.pac-container') ||
              target.classList.contains('pac-item') ||
              target.classList.contains('pac-item-query') ||
              target.classList.contains('pac-matched')
            ) {
              e.preventDefault();
            }
          }}
        >
          <div className="grid h-full grid-rows-[auto_1fr_auto]">
            {/* Header */}
            <SheetHeader className="border-b bg-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-orange-100 p-2">
                    <MapPin className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <SheetTitle className="text-xl font-semibold text-gray-900">
                      {isEditing ? location?.name || 'Edit Location' : 'Create New Location'}
                    </SheetTitle>
                    {isEditing && location && (
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>ID: {location._id}</span>
                        <StatusDisplay
                          status={location.isActive ? 'active' : 'suspended'}
                          displayTextOverride={location.isActive ? 'Active' : 'Inactive'}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </SheetHeader>

            {/* Content Area - Scrollable */}
            <div className="overflow-y-auto px-6 py-6">
              <LocationForm
                location={location}
                onStateChange={(state) => {
                  setIsFormValid(state.isValid);
                  setIsFormDirty(state.isDirty);
                  setFormValues(state.values);
                }}
                ref={formRef}
              />
            </div>

            {/* Footer */}
            <SheetFooter className="border-t bg-gray-50 px-6 py-4">
              <div className="flex w-full items-center justify-between">
                {isEditing && location && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Last updated:{' '}
                      {location.updatedAt ? formatDate(location.updatedAt) : 'Unknown'}
                    </span>
                  </div>
                )}
                <div className="flex space-x-2 ml-auto">
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    disabled={!canSave}
                    className="bg-orange-600 hover:bg-orange-700"
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader h={4} w={4} color="white" />
                        {isEditing ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>{isEditing ? 'Update Location' : 'Create Location'}</>
                    )}
                  </Button>
                </div>
              </div>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>

      {confirmDialog}
    </>
  );
};
